import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Boxes, FileText, Factory, Wallet } from "lucide-react";

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="surface-card p-5" data-testid={`vendor-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between"><span className="label-overline">{label}</span><Icon className="w-4 h-4 text-primary" /></div>
      <div className="font-display text-2xl sm:text-3xl font-black mt-3">{value}</div>
    </div>
  );
}

export default function VendorOverview() {
  const { t } = useTranslation();
  const { user, tenant } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    (async () => {
      const [c, r, o] = await Promise.all([
        api.get("/vendor/catalog").catch(() => ({ data: [] })),
        api.get("/rfq").catch(() => ({ data: [] })),
        api.get("/orders").catch(() => ({ data: [] })),
      ]);
      setCatalog(c.data); setRfqs(r.data); setOrders(o.data);
    })();
  }, []);

  const revenue = orders.reduce((s, o) => s + (o.total_price || 0), 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8" data-testid="vendor-overview">
      <div>
        <div className="label-overline">{tenant?.name}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black mt-2">{t("dashboards.vendor.welcome", { name: user?.full_name?.split(" ")[0] || "" })}</h1>
        <p className="text-muted-foreground mt-2">{t("dashboards.vendor.subtitle")}</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Catalog items" value={catalog.length} icon={Boxes} />
        <Stat label="Open RFQs" value={rfqs.filter(r => r.status === "OPEN").length} icon={FileText} />
        <Stat label="Active orders" value={orders.filter(o => o.current_stage !== "DISPATCHED").length} icon={Factory} />
        <Stat label="Order revenue" value={`₹${revenue.toLocaleString()}`} icon={Wallet} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="surface-card p-6">
          <h2 className="font-display text-xl font-bold">Recent catalog</h2>
          <div className="mt-4 space-y-3">
            {catalog.slice(0, 5).map((c) => (
              <div key={c.id} className="flex items-center justify-between text-sm border-b border-border pb-2 last:border-0">
                <div>
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{c.tile_size} · {c.finish} · B-{c.batch_number}</div>
                </div>
                <div className="font-mono text-xs">{c.boxes_available} boxes</div>
              </div>
            ))}
            {catalog.length === 0 && <div className="text-sm text-muted-foreground py-2">No items yet. Add your first tile in Catalog.</div>}
          </div>
        </div>

        <div className="surface-card p-6">
          <h2 className="font-display text-xl font-bold">Recent orders</h2>
          <div className="mt-4 space-y-3">
            {orders.slice(0, 5).map((o) => (
              <div key={o.id} className="flex items-center justify-between text-sm border-b border-border pb-2 last:border-0">
                <div>
                  <div className="font-medium">{o.tile_size} · {o.finish}</div>
                  <div className="text-xs text-muted-foreground font-mono">{o.quantity_boxes} boxes · {o.current_stage}</div>
                </div>
                <div className="font-mono text-xs">₹{(o.total_price || 0).toLocaleString()}</div>
              </div>
            ))}
            {orders.length === 0 && <div className="text-sm text-muted-foreground py-2">No orders yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
